/**
 * projectSetupService - Creates starter content for new project spaces.
 *
 * Each setup function receives an already-created spaceId and a project name,
 * then populates the space with the appropriate entities.
 */

const getCommandRegistry = async () => {
  const { useCommandRegistryStore } = await import('../stores/commandRegistryStore');
  return useCommandRegistryStore.getState().executeCommand;
};

const getFolderId = (result: any): string | undefined =>
  result?.data?.folder?.id || result?.data?.folder?._id;

/** Book project: Writers Dashboard + full folder structure + starter docs + BookProject */
export async function setupBookProject(spaceId: string, projectName: string): Promise<void> {
  const executeCommand = await getCommandRegistry();

  const run = (cmd: string, params: Record<string, any>) =>
    executeCommand(cmd, { ...params, spaceId, createWindow: false }) as any;

  // Writers Dashboard — pos '!' pins it first in the sidebar
  await run('writers_dashboard_create', { title: 'Writing Hub' });

  // Root-level folders get sequential pos values so sidebar order is deterministic
  const brainstorm = await run('folder_create', { name: 'Brainstorm', color: '#3b82f6', edge_pos: 'A' });
  const brainstormId = getFolderId(brainstorm);
  await run('notes_create', { title: 'Brainstorm Notes', folderId: brainstormId });
  await run('canvas_create', { title: 'Brainstorm Canvas', folderId: brainstormId });

  await run('folder_create', { name: 'Research', color: '#f59e0b', edge_pos: 'B' });

  const bible = await run('folder_create', { name: 'World & Character Bible', color: '#8b5cf6', edge_pos: 'C' });
  await run('docs_create', { name: 'World & Character Bible', folderId: getFolderId(bible) });

  const outline = await run('folder_create', { name: 'Outline', color: '#10b981', edge_pos: 'D' });
  const outlineId = getFolderId(outline);
  await run('docs_create', { name: 'Outline', folderId: outlineId });
  await run('canvas_create', { title: 'Story Structure', folderId: outlineId });

  const draft = await run('folder_create', { name: 'Drafts', color: '#0d9488', edge_pos: 'E' });
  const draftId = getFolderId(draft);
  for (let i = 1; i <= 5; i++) {
    await run('docs_create', { name: `Chapter ${i}`, folderId: draftId });
  }

  const publishPrep = await run('folder_create', { name: 'Publish Prep', color: '#6366f1', edge_pos: 'F' });
  await run('docs_create', { name: 'KDP Metadata', folderId: getFolderId(publishPrep) });

  // Book project entity — pos 'G' places it after all folders
  const { entityService } = await import('./entityService');
  const now = new Date().toISOString();
  await entityService.create({
    entityType: 'BookProject',
    name: projectName,
    spaceId,
    edge_pos: 'G',
    skeleton: {
      '@type': 'BookProject',
      title: projectName,
      author: '',
      formatSettings: {
        pageSize: '6x9',
        orientation: 'portrait',
        margins: { top: 0.75, bottom: 0.75, inside: 0.75, outside: 0.5 },
        bodyFont: 'Garamond',
        bodyFontSize: 11,
        lineHeight: 1.5,
        headingFont: 'Garamond',
        headingFontSize: 16,
        chapterPageBreak: true,
        header: { enabled: false, content: '', position: 'top' },
        footer: { enabled: true, content: 'pageNumber', position: 'bottom' },
        tableOfContents: { enabled: true, style: 'standard' }
      },
      chapters: [],
      createdAt: now,
      updatedAt: now
    }
  } as any);
}

/** Writing project: outline doc at root, brainstorm, research, drafts */
export async function setupWritingProject(spaceId: string, projectName: string): Promise<void> {
  const executeCommand = await getCommandRegistry();

  const run = (cmd: string, params: Record<string, any>) =>
    executeCommand(cmd, { ...params, spaceId, createWindow: false }) as any;

  // Root-level outline doc — structure the piece before writing
  await run('docs_create', { name: 'Outline' });

  // Brainstorm folder — raw ideas before drafting
  const brainstorm = await run('folder_create', { name: 'Brainstorm', color: '#ec4899' });
  await run('notes_create', { title: 'Brainstorm Notes', folderId: getFolderId(brainstorm) });
  await run('canvas_create', { title: 'Brainstorm Canvas', folderId: getFolderId(brainstorm) });

  // Research folder — references, world-building, facts
  await run('folder_create', { name: 'Research', color: '#f59e0b' });

  // Drafts folder — the actual writing
  const drafts = await run('folder_create', { name: 'Drafts', color: '#0d9488' });
  await run('docs_create', { name: `${projectName} — Draft 1`, folderId: getFolderId(drafts) });
}

/** Business project: outline doc at root, brainstorm, research, drafts */
export async function setupBusinessProject(spaceId: string, projectName: string): Promise<void> {
  const executeCommand = await getCommandRegistry();

  const run = (cmd: string, params: Record<string, any>) =>
    executeCommand(cmd, { ...params, spaceId, createWindow: false }) as any;

  // Root-level overview presentation
  await run('presentations_create', { title: 'Overview' });

  // Root-level timeline sheet — track milestones and deadlines
  await run('sheets_create', { name: 'Timeline' });

  // Brainstorm folder — ideas, angles, key messages, and outline
  const brainstorm = await run('folder_create', { name: 'Brainstorm', color: '#ec4899' });
  await run('notes_create', { title: 'Brainstorm Notes', folderId: getFolderId(brainstorm) });
  await run('docs_create', { name: 'Outline', folderId: getFolderId(brainstorm) });

  // Research folder — data, competitive landscape, background material
  const research = await run('folder_create', { name: 'Research', color: '#f59e0b' });
  await run('notes_create', { title: 'Research Notes', folderId: getFolderId(research) });

  // Drafts folder — drafts of the proposal, plan, or presentation
  const drafts = await run('folder_create', { name: 'Drafts', color: '#0d9488' });
  await run('docs_create', { name: `${projectName} — Draft 1`, folderId: getFolderId(drafts) });
}
